import { useState, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { playOrderReadySound } from '@/utils/soundUtils';
import { completeTableSession } from '@/services/menuService';
import { useCacheInvalidation } from '@/hooks/useCache';
import type { Order } from '@/types/index';

export const statusConfig: { [key: string]: { label: string; color: string; bgColor: string; textColor: string; } } = {
    pending: { label: 'Pending', color: 'bg-yellow-500', bgColor: 'bg-yellow-50', textColor: 'text-yellow-800' },
    confirmed: { label: 'Confirmed', color: 'bg-blue-500', bgColor: 'bg-blue-50', textColor: 'text-blue-800' },
    preparing: { label: 'Preparing', color: 'bg-orange-500', bgColor: 'bg-orange-50', textColor: 'text-orange-800' },
    ready: { label: 'Ready', color: 'bg-green-500', bgColor: 'bg-green-50', textColor: 'text-green-800' },
    completed: { label: 'Completed', color: 'bg-gray-500', bgColor: 'bg-gray-50', textColor: 'text-gray-800' },
    cancelled: { label: 'Cancelled', color: 'bg-red-500', bgColor: 'bg-red-50', textColor: 'text-red-800' },
};

interface UseOrderActionsProps {
    branchId?: string;
    orders: Order[];
    updateOrderStatus: (orderId: string, status: string) => Promise<{ success: boolean; error?: string }>;
    refetch: () => Promise<void>;
    clearOptimisticUpdate: (orderId: string) => void;
    soundEnabled: boolean;
}

export const useOrderActions = ({
    branchId,
    orders,
    updateOrderStatus,
    refetch,
    clearOptimisticUpdate,
    soundEnabled
}: UseOrderActionsProps) => {
    const { toast } = useToast();
    const { invalidateCache } = useCacheInvalidation();

    // Local state for dialogs triggered by actions
    const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
    const [orderToComplete, setOrderToComplete] = useState<Order | null>(null);

    const handleStatusUpdate = useCallback(async (orderId: string, newStatus: string, sessionId?: string) => {
        const result = await updateOrderStatus(orderId, newStatus);
        if (result.success) {
            let toastMessage = `Order marked as ${statusConfig[newStatus]?.label}`;
            if (newStatus === 'confirmed') {
                toastMessage += '. Auto-printing receipt if enabled.';
            }

            toast({
                title: "Status Updated",
                description: toastMessage,
                duration: 2000
            });

            if (soundEnabled && newStatus === 'ready') {
                await playOrderReadySound();
            }

            if ((newStatus === 'completed' || newStatus === 'cancelled') && sessionId) {
                console.log('[Session End] Calling completeTableSession for sessionId:', sessionId);
                await completeTableSession(sessionId, newStatus as 'completed' | 'cancelled');
            }
        } else {
            console.error('Status update failed:', result.error);
            toast({
                title: "Error Updating Order Status",
                description: result.error || "Failed to update order status. Please try again.",
                variant: "destructive",
                duration: 5000
            });
        }
    }, [updateOrderStatus, soundEnabled, toast]);

    const handleQuickAction = useCallback(async (orderId: string, action: string, sessionId?: string) => {
        let newStatus = '';
        switch (action) {
            case 'confirm':
                newStatus = 'confirmed';
                break;
            case 'prepare':
                newStatus = 'preparing';
                break;
            case 'ready':
                newStatus = 'ready';
                break;
            case 'complete': {
                const order = orders.find(o => o.id === orderId);
                if (order) {
                    setOrderToComplete(order);
                    setCheckoutDialogOpen(true);
                }
                return;
            }
            case 'cancel':
                newStatus = 'cancelled';
                break;
            default:
                return;
        }

        await handleStatusUpdate(orderId, newStatus, sessionId);
    }, [orders, handleStatusUpdate]);

    const handleOrderCompleted = useCallback(async () => {
        console.log('🎉 Order completed callback triggered');

        if (orderToComplete?.id) {
            console.log('🧹 Clearing optimistic updates for completed order:', orderToComplete.id);
            clearOptimisticUpdate(orderToComplete.id);
        }

        setOrderToComplete(null);
        setCheckoutDialogOpen(false);

        if (branchId) {
            console.log('🗑️ Invalidating cache for orders and table sessions');
            await invalidateCache(['orders', 'tableSessionSummary'], branchId);
        }

        console.log('🔄 Forcing immediate refetch after order completion');
        await refetch();

        setTimeout(async () => {
            console.log('🔄 Secondary refetch for data consistency');
            await refetch();
        }, 1000);
    }, [orderToComplete, clearOptimisticUpdate, branchId, invalidateCache, refetch]);

    return {
        handleQuickAction,
        handleStatusUpdate,
        handleOrderCompleted,
        checkoutDialogOpen,
        setCheckoutDialogOpen,
        orderToComplete,
        setOrderToComplete
    };
};
